import { telephonyProviders } from "@/src/constants/telephonyConstants";
import { telephonyDeviceRefreshInterval } from "@/src/constants/telephonyConstants.js";
import store from "@/src/store";
import providerManager from "@/src/services/telephony/providerManager";
import telephonyFactory from "@/src/services/telephony/base/telephonyFactory";
import providerFeatureManager from "@/src/services/telephony/providers/providerFeatureManager";
import PlivoIframeDevice from "@/src/services/telephony/providers/plivo/plivoIframeDevice";

// Promise.allSettled status constants
const promiseStatuses = {
  FULFILLED: "fulfilled",
  REJECTED: "rejected",
};

class TelephonyDevice {
  constructor() {
    this.devices = {};
    this.currentProvider = null;
    this.refreshInterval = null;
    this.lastRefreshTime = Date.now();
  }

  async initialize(config = {}) {
    try {
      this.config = config;
      const initializationPromises = [];
      const providerOrder = [];

      if (config.twilioToken) {
        providerOrder.push(telephonyProviders.twilio);
        initializationPromises.push(
          this._initializeProvider(
            telephonyProviders.twilio,
            config.twilioToken,
          ),
        );
      }

      const plivoInitPromise = (async () => {
        const plivoResults = [];

        if (
          config.plivoCredentials?.username &&
          config.plivoCredentials?.password
        ) {
          providerOrder.push(telephonyProviders.plivo);
          try {
            await this._initializeProvider(
              telephonyProviders.plivo,
              config.plivoCredentials,
            );
            plivoResults.push({
              status: promiseStatuses.FULFILLED,
              value: undefined,
            });
          } catch (error) {
            plivoResults.push({
              status: promiseStatuses.REJECTED,
              reason: error,
            });
          }
        }

        if (
          config.plivoInternationalCredentials?.username &&
          config.plivoInternationalCredentials?.password
        ) {
          providerOrder.push(telephonyProviders.plivoInternational);
          try {
            // Initialize plivoInternational in iframe
            const iframeDevice = new PlivoIframeDevice(
              telephonyProviders.plivoInternational,
            );
            await iframeDevice.initialize(config.plivoInternationalCredentials);
            this.devices[telephonyProviders.plivoInternational] = iframeDevice;
            plivoResults.push({
              status: promiseStatuses.FULFILLED,
              value: undefined,
            });
            console.log(
              "[TelephonyDevice] PlivoInternational initialized in iframe",
            );
          } catch (error) {
            plivoResults.push({
              status: promiseStatuses.REJECTED,
              reason: error,
            });
            console.error(
              "[TelephonyDevice] PlivoInternational iframe initialization failed:",
              error,
            );
          }
        }

        return plivoResults;
      })();

      initializationPromises.push(plivoInitPromise);

      const allResults = await Promise.allSettled(initializationPromises);

      const results = [];
      for (const result of allResults) {
        if (
          result.status === promiseStatuses.FULFILLED &&
          Array.isArray(result.value)
        ) {
          results.push(...result.value);
        } else {
          results.push(result);
        }
      }

      results.forEach((result, index) => {
        const provider = providerOrder[index];
        if (result.status === promiseStatuses.REJECTED) {
          console.error(`Failed to initialize ${provider}:`, result.reason);
        } else {
          console.log(`Successfully initialized ${provider}`);
        }
      });

      const successfulInitializations = results.filter(
        (result) => result.status === promiseStatuses.FULFILLED,
      );

      if (successfulInitializations.length === 0) {
        throw new Error("Failed to initialize any telephony providers");
      }

      this.currentProvider = store.getters["telephony/getCurrentProvider"];

      console.log(
        `Initialized ${successfulInitializations.length} out of ${results.length} providers`,
      );

      this.startRefreshTimer();
      this.lastRefreshTime = Date.now();
    } catch (error) {
      console.error("Failed to initialize telephony device:", error);
      throw error;
    }
  }

  async _initializeProvider(provider, credentials) {
    try {
      console.log(`Initializing provider: ${provider}`);

      const device = telephonyFactory.createDevice(provider);

      await store.dispatch("telephony/setProviderInitialized", {
        provider,
        isInitialized: false,
      });

      await device.initialize(credentials);

      this.devices[provider] = device;

      await store.dispatch("telephony/setProviderInitialized", {
        provider,
        isInitialized: true,
      });
      await store.dispatch("telephony/setProviderDeviceReady", {
        provider,
        deviceReady: true,
      });

      console.log(`Successfully initialized provider: ${provider}`);
    } catch (error) {
      console.error(`Failed to initialize ${provider}:`, error);

      await store.dispatch("telephony/setProviderInitialized", {
        provider,
        isInitialized: false,
      });
      await store.dispatch("telephony/setProviderDeviceReady", {
        provider,
        deviceReady: false,
      });

      throw error;
    }
  }

  _getDeviceForProvider(provider) {
    if (!this.devices[provider]) {
      throw new Error(`Provider ${provider} not initialized`);
    }
    return this.devices[provider];
  }

  _selectProviderForCall(params) {
    return providerManager.getProviderForNumber(params.From);
  }

  getAvailableProviders() {
    return Object.keys(this.devices).filter((provider) => {
      const device = this.devices[provider];
      return device && device.getDeviceStatus().isReady;
    });
  }

  async makeCall(params) {
    try {
      const provider = this._selectProviderForCall(params);

      console.log(
        `[TelephonyDevice] Outgoing call - Provider: ${provider}, From: ${params.From}, To: ${params.To}`,
      );

      if (!this.devices[provider]) {
        throw new Error(`Provider ${provider} not available`);
      }

      store.commit("telephony/setProvider", provider);
      this.currentProvider = provider;

      console.log(
        `Selected provider ${this.currentProvider} for call from ${params.From} to ${params.To}`,
      );

      const device = this._getDeviceForProvider(this.currentProvider);

      try {
        return await device.makeCall(params);
      } catch (error) {
        if (
          error.name === "NotFoundError" ||
          error.name === "NotReadableError"
        ) {
          await this.handleAudioDeviceError(error);
        }
        throw error;
      }
    } catch (error) {
      return this._handleFeatureError("makeCall", error, [params]);
    }
  }

  async hangupCall() {
    try {
      console.log("TelephonyDevice.hangupCall() called");
      console.log("Current provider:", this.currentProvider);

      if (!this.currentProvider) {
        console.warn("No active provider found, searching all providers...");

        for (const provider of Object.keys(this.devices)) {
          const device = this.devices[provider];
          const status = device.getDeviceStatus();
          console.log(`${provider} device status:`, status);

          if (status.hasActiveCall || status.hasIncomingCall) {
            console.log(
              `Found active call on ${provider}, setting as current provider`,
            );
            this.currentProvider = provider;
            break;
          }
        }

        if (!this.currentProvider) {
          throw new Error("No active provider with ongoing calls found");
        }
      }

      const device = this._getDeviceForProvider(this.currentProvider);
      const status = device.getDeviceStatus();

      console.log("Device status before hangup:", status);
      console.log(
        "Calling hangup on device for provider:",
        this.currentProvider,
      );

      if (status.hasActiveCall) {
        console.log("Hanging up active call...");
        return await device.hangupCall();
      } else if (status.hasIncomingCall) {
        console.log("Hanging up incoming call...");
        if (device.hangupIncomingCall) {
          return await device.hangupIncomingCall();
        } else {
          return await device.hangupCall();
        }
      } else {
        console.warn(
          "No active or incoming calls found, calling generic hangup",
        );
        return await device.hangupCall();
      }
    } catch (error) {
      console.error("Error in telephonyDevice.hangupCall:", error);
      return this._handleFeatureError("hangupCall", error);
    }
  }

  async answerIncomingCall() {
    try {
      let providerToUse = null;

      console.log("Searching for provider with incoming call...");
      for (const provider of Object.keys(this.devices)) {
        const device = this.devices[provider];
        const status = device.getDeviceStatus();
        console.log(`${provider} status:`, status);

        if (status.hasIncomingCall) {
          providerToUse = provider;
          console.log(`Found incoming call on provider: ${provider}`);
          break;
        }
      }

      if (!providerToUse) {
        for (const provider of Object.keys(this.devices)) {
          const device = this.devices[provider];
          const incomingConnection = device.getIncomingConnection();
          if (incomingConnection) {
            providerToUse = provider;
            console.log(`Found incoming connection on provider: ${provider}`);
            break;
          }
        }
      }

      if (!providerToUse) {
        throw new Error("No provider with incoming calls found");
      }

      console.log(`Answering incoming call on provider: ${providerToUse}`);
      this.currentProvider = providerToUse;
      store.commit("telephony/setProvider", providerToUse);

      const device = this._getDeviceForProvider(providerToUse);

      try {
        const result = await device.answerIncomingCall();

        if (result) {
          console.log(
            `Successfully answered incoming call on ${providerToUse}`,
          );
          store.commit("calls/SET_IS_CALL_CONNECTED", true);
        }

        return result;
      } catch (error) {
        if (
          error.name === "NotFoundError" ||
          error.name === "NotReadableError"
        ) {
          await this.handleAudioDeviceError(error);
        }
        throw error;
      }
    } catch (error) {
      console.error("Error answering incoming call:", error);
      return this._handleFeatureError("answerIncomingCall", error);
    }
  }

  async rejectIncomingCall() {
    try {
      let providerToUse = this.currentProvider;

      if (!providerToUse) {
        for (const provider of Object.keys(this.devices)) {
          const device = this.devices[provider];
          const status = device.getDeviceStatus();

          if (status.hasIncomingCall) {
            providerToUse = provider;
            console.log(
              `Found incoming call to reject on provider: ${provider}`,
            );
            break;
          }
        }

        if (!providerToUse) {
          throw new Error("No provider with incoming calls found");
        }
      }

      const device = this._getDeviceForProvider(providerToUse);
      return await device.rejectIncomingCall();
    } catch (error) {
      console.error("Error rejecting incoming call:", error);
      return this._handleFeatureError("rejectIncomingCall", error);
    }
  }

  async toColdTransfer(params) {
    try {
      if (!this.currentProvider) {
        throw new Error("No active provider");
      }
      if (
        !providerFeatureManager.isFeatureAvailable(
          this.currentProvider,
          "coldTransfer",
        )
      ) {
        throw new Error(
          "Cold transfer feature is not available for current provider",
        );
      }
      const device = this._getDeviceForProvider(this.currentProvider);
      return await device.toColdTransfer(params);
    } catch (error) {
      return this._handleFeatureError("toColdTransfer", error, [params]);
    }
  }

  async toWarmTransfer(params) {
    try {
      if (!this.currentProvider) {
        throw new Error("No active provider");
      }
      if (
        !providerFeatureManager.isFeatureAvailable(
          this.currentProvider,
          "warmTransfer",
        )
      ) {
        throw new Error(
          "Warm transfer feature is not available for current provider",
        );
      }
      const device = this._getDeviceForProvider(this.currentProvider);
      return await device.toWarmTransfer(params);
    } catch (error) {
      return this._handleFeatureError("toWarmTransfer", error, [params]);
    }
  }

  async swapCalls() {
    try {
      if (!this.currentProvider) {
        throw new Error("No active provider");
      }
      if (
        !providerFeatureManager.isFeatureAvailable(
          this.currentProvider,
          "callSwap",
        )
      ) {
        throw new Error(
          "Call swap feature is not available for current provider",
        );
      }
      const device = this._getDeviceForProvider(this.currentProvider);
      return await device.swapCalls();
    } catch (error) {
      return this._handleFeatureError("swapCalls", error);
    }
  }

  async mergeConference() {
    try {
      if (!this.currentProvider) {
        throw new Error("No active provider");
      }
      if (
        !providerFeatureManager.isFeatureAvailable(
          this.currentProvider,
          "warmTransfer",
        )
      ) {
        throw new Error(
          "Merge conference feature is not available for current provider",
        );
      }
      const device = this._getDeviceForProvider(this.currentProvider);
      return await device.mergeConference();
    } catch (error) {
      return this._handleFeatureError("mergeConference", error);
    }
  }

  async startRecording() {
    try {
      if (!this.currentProvider) {
        throw new Error("No active provider");
      }
      if (
        !providerFeatureManager.isFeatureAvailable(
          this.currentProvider,
          "callRecording",
        )
      ) {
        throw new Error(
          "Call recording feature is not available for current provider",
        );
      }
      const device = this._getDeviceForProvider(this.currentProvider);
      return await device.startRecording();
    } catch (error) {
      return this._handleFeatureError("startRecording", error);
    }
  }

  async stopRecording() {
    try {
      if (!this.currentProvider) {
        throw new Error("No active provider");
      }
      if (
        !providerFeatureManager.isFeatureAvailable(
          this.currentProvider,
          "callRecording",
        )
      ) {
        throw new Error(
          "Call recording feature is not available for current provider",
        );
      }
      const device = this._getDeviceForProvider(this.currentProvider);
      return await device.stopRecording();
    } catch (error) {
      return this._handleFeatureError("stopRecording", error);
    }
  }

  async getVoicemails() {
    try {
      if (!this.currentProvider) {
        throw new Error("No active provider");
      }
      if (
        !providerFeatureManager.isFeatureAvailable(
          this.currentProvider,
          "voicemails",
        )
      ) {
        throw new Error(
          "Voicemails feature is not available for current provider",
        );
      }
      const device = this._getDeviceForProvider(this.currentProvider);
      return await device.getVoicemails();
    } catch (error) {
      return this._handleFeatureError("getVoicemails", error);
    }
  }

  async purchasePhoneNumber(params) {
    try {
      if (!this.currentProvider) {
        throw new Error("No active provider");
      }
      if (
        !providerFeatureManager.isFeatureAvailable(
          this.currentProvider,
          "purchasePhoneNumber",
        )
      ) {
        throw new Error(
          "Phone number purchase feature is not available for current provider",
        );
      }
      const device = this._getDeviceForProvider(this.currentProvider);
      return await device.purchasePhoneNumber(params);
    } catch (error) {
      return this._handleFeatureError("purchasePhoneNumber", error, [params]);
    }
  }

  async deletePhoneNumber(params) {
    try {
      if (!this.currentProvider) {
        throw new Error("No active provider");
      }
      if (
        !providerFeatureManager.isFeatureAvailable(
          this.currentProvider,
          "deletePhoneNumber",
        )
      ) {
        throw new Error(
          "Phone number deletion feature is not available for current provider",
        );
      }
      const device = this._getDeviceForProvider(this.currentProvider);
      return await device.deletePhoneNumber(params);
    } catch (error) {
      return this._handleFeatureError("deletePhoneNumber", error, [params]);
    }
  }

  getDeviceStatus() {
    const allStatuses = {};
    let hasActiveCall = false;
    let hasIncomingCall = false;
    let isReady = false;

    Object.keys(this.devices).forEach((provider) => {
      const status = this.devices[provider].getDeviceStatus();
      allStatuses[provider] = status;

      if (status.hasActiveCall) hasActiveCall = true;
      if (status.hasIncomingCall) hasIncomingCall = true;
      if (status.isReady) isReady = true;
    });

    console.log("All device statuses:", allStatuses);

    if (this.currentProvider && this.devices[this.currentProvider]) {
      return this.devices[this.currentProvider].getDeviceStatus();
    }

    return { isReady, hasActiveCall, hasIncomingCall };
  }

  getCurrentConnection() {
    if (this.currentProvider && this.devices[this.currentProvider]) {
      return this.devices[this.currentProvider].getCurrentConnection();
    }

    for (const provider of Object.keys(this.devices)) {
      const connection = this.devices[provider].getCurrentConnection();
      if (connection) {
        return connection;
      }
    }

    return null;
  }

  getIncomingConnection() {
    for (const provider of Object.keys(this.devices)) {
      try {
        const connection = this.devices[provider].getIncomingConnection();
        if (connection) {
          console.log(`Found incoming connection on provider: ${provider}`);
          if (!this.currentProvider) {
            this.currentProvider = provider;
            store.commit("telephony/setProvider", provider);
          }
          return connection;
        }
      } catch (error) {
        console.error(
          `Error checking incoming connection for ${provider}:`,
          error,
        );
      }
    }

    return null;
  }

  async _handleFeatureError(feature, error, args = []) {
    console.error(`Feature '${feature}' failed:`, error);

    if (error.message.includes("not available")) {
      console.warn(
        `Feature '${feature}' is not available for current provider`,
      );
    } else if (error.message.includes("not ready")) {
      console.warn("Telephony device is not ready");
    }

    throw error;
  }

  async handleAudioDeviceError(error) {
    console.error("Audio device error:", error);

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput",
      );
      const audioOutputs = devices.filter(
        (device) => device.kind === "audiooutput",
      );

      console.log("Available audio inputs:", audioInputs.length);
      console.log("Available audio outputs:", audioOutputs.length);

      if (audioInputs.length === 0) {
        console.error("No audio input devices found");
      }
    } catch (enumError) {
      console.error("Failed to enumerate devices:", enumError);
    }
  }

  startRefreshTimer() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    this.refreshInterval = setInterval(async () => {
      if (this.canRefreshDevices()) {
        console.log("Performing scheduled refresh of telephony devices...");
        await this.refreshAllDevices();
      } else {
        console.log("Skipping refresh due to active call or campaign");
      }
    }, telephonyDeviceRefreshInterval);
  }

  canRefreshDevices() {
    const hasActiveCall = store.getters["calls/hasActiveCall"];
    const isCallConnected = store.getters["calls/isCallConnected"];
    const isCurrentCampaign = store.getters["callsCampaign/isCurrentCampaign"];
    const callOngoing = store.getters["callsCampaign/callOngoing"];

    const hasAnyDeviceActivity = Object.values(this.devices).some((device) => {
      const status = device.getDeviceStatus();
      return status.hasActiveCall || status.hasIncomingCall;
    });

    return (
      !hasActiveCall &&
      !isCallConnected &&
      !isCurrentCampaign &&
      !callOngoing &&
      !hasAnyDeviceActivity
    );
  }

  async refreshAllDevices() {
    console.log("Refreshing all telephony devices...");
    const refreshPromises = [];

    for (const provider of Object.keys(this.devices)) {
      const device = this.devices[provider];
      if (device && typeof device.refresh === "function") {
        refreshPromises.push(
          device.refresh().catch((error) => {
            console.error(`Failed to refresh ${provider}:`, error);
          }),
        );
      }
    }

    await Promise.allSettled(refreshPromises);
    this.lastRefreshTime = Date.now();
    console.log("Device refresh completed");
  }

  async manualRefresh() {
    if (!this.canRefreshDevices()) {
      console.warn(
        "Cannot refresh devices: active call or campaign in progress",
      );
      return false;
    }
    await this.refreshAllDevices();
    return true;
  }

  destroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }

    for (const device of Object.values(this.devices)) {
      if (device && typeof device.destroy === "function") {
        try {
          device.destroy();
        } catch (error) {
          console.error("Error destroying device:", error);
        }
      }
    }

    this.devices = {};
    this.currentProvider = null;
  }
}

export default new TelephonyDevice();
